import React, { useEffect, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "../styles.css"

interface Props {
    titleGraph: string;
    data: any;
    colorIcons?: string;
    axisLabels?: string[];
    fullWidth?: boolean;
    exportPDF?: boolean;
    tooltipLabels?: string[];
    tooltipValues?: string[];
    info?: any[];
    monoColor?: boolean;
}

const ChartBar: React.FC<Props> = ({ titleGraph, data, fullWidth, colorIcons, axisLabels, exportPDF, tooltipLabels, tooltipValues, info, monoColor = false }) => {
    Chart.register(ChartDataLabels);
    Chart.defaults.font.size = 12;

    const monoColors = [
        "#57CE71",
        "#5AC8FA",
        "#FF9501",
        "#FF3B30",
        "#5856D6",
        "#8A2BE2",
        "#FFCC00",

    ];

    const backgroundColor = useMemo(() => {
        if (monoColor) {
            return monoColors[Math.floor(Math.random() * monoColors.length)];
        }
        if (colorIcons) {
            return ["#57CE71", "#5AC8FA", "#FF9501"];
        }
        return [
            "#57CE71",
            "#5AC8FA",
            "#FF9501",
            "#4A90E2",
            "#FF3B30",
            "#5AC8FA",
            "#FFCC00",
            "#5856D6",
            "#34C759",
            "#FF2D55",
            "#007AFF",
            "#FF2D55",
            "#007AFF",
            "#FFCC00",
            "#5856D6",
            "#34C759",
            "#2737FA",
            "#8A2BE2",
        ];
    }, [monoColor, colorIcons]);

    const chartData = {
        labels: data ? Array.from(data, (el: any) => el?.label) : [],
        datasets: [
            {
                label: colorIcons,
                data: data ? data.map((el: any) => Number(el?.value.toPrecision(2))) : [],
                backgroundColor: backgroundColor,
                borderWidth: 1,
            },
        ],

    }

    if (!data) return <></>

    return (
        <div>
            <span className='titleGraph'>{titleGraph}</span>
            <div className={`contentGraphBorder ${fullWidth ? `containerGraphFull${exportPDF ? "-print" : ""}` : ""}`}>
                {data.every((el: any) => el?.value === 0) ?
                    <div className='noData'>
                        <span>Sem dados</span>
                    </div>
                    :
                    <>
                        <Bar
                            className={`${fullWidth ? "fullWidth" : ""}`}
                            data={chartData}
                            options={{
                                devicePixelRatio: 4,
                                animation: {
                                    duration: 0
                                },
                                plugins: {
                                    legend: {
                                        display: !!colorIcons,
                                        position: 'bottom',
                                        labels: {
                                            boxWidth: 10,
                                        },
                                    },
                                    datalabels: {
                                        display: true,
                                        anchor: "end",
                                        align: "start",
                                        offset: -20,
                                        font: {
                                            size: 12,
                                            weight: "bold"
                                        },
                                    },
                                    tooltip: {
                                        enabled: true,
                                        mode: 'index',
                                        intersect: false,
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        titleColor: '#fff',
                                        bodyColor: '#fff',
                                        borderColor: '#fff',
                                        borderWidth: 1,
                                        displayColors: false,
                                        callbacks: {
                                            title: function (context) {
                                                return tooltipLabels && tooltipLabels[context[0].dataIndex]
                                                    ? tooltipLabels[context[0].dataIndex]
                                                    : context[0].label;
                                            },
                                            label: function (context) {
                                                if (info && Array.isArray(info) && info[context.dataIndex]) {
                                                    const microAreas = info[context.dataIndex];
                                                    if (Array.isArray(microAreas)) {
                                                        if (context.datasetIndex === 0) { // Bar chart
                                                            return microAreas.map((microArea: any) => {
                                                                const value = microArea.value !== null ? microArea.value : '0';
                                                                return `${microArea.initials}: ${value}`;
                                                            });
                                                        } else {
                                                            return '';
                                                        }
                                                    }
                                                }

                                                if (tooltipLabels && tooltipValues) {
                                                    const labelIndex = context.dataIndex;
                                                    const label = tooltipLabels[labelIndex] || context.dataset.label || '';
                                                    const value = tooltipValues[labelIndex] || context.parsed.y;
                                                    return `${label}: ${value}`;
                                                }

                                                const label = context.dataset.label || '';
                                                const value = context.parsed.y;
                                                return `${label}: ${value}`;
                                            }
                                        }
                                    },
                                },
                                layout: {
                                    padding: {
                                        top: 30,
                                        right: 2
                                    }
                                },
                                scales: {
                                    x: {
                                        grid: {
                                            display: false,
                                        },
                                        title: {
                                            display: !!axisLabels,
                                            text: axisLabels ? axisLabels[0] : "",
                                            font: {
                                                size: 12,
                                            },
                                        }
                                    },
                                    y: {
                                        title: {
                                            display: !!axisLabels,
                                            text: axisLabels ? axisLabels[1] : "",
                                            font: {
                                                size: 12,
                                            },
                                        }
                                    }
                                }
                            }}
                            width={fullWidth ? 1360 : 285}
                            height={fullWidth ? 500 : 285}
                        />
                    </>
                }
            </div>
        </div>
    )
};

export default ChartBar;